export function useCartApi(): CartApi {
  const cartStore = useCartStore();
  const defaultPaginationSize = Number(useRuntimeConfig().public.defaultPaginationSize);
  const currency = useRuntimeConfig().public.currency as Currency;
  const { ensureAuthenticated } = useDjustAuth();
  /**
   * Fetch a cart by ID
   */
  const getCartbyId = async (
    params: GetCartRequest,
    useStore = true
  ): Promise<GetCartbyIDResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate that cartId is provided
      const { cartId, currency } = params;
      if (!cartId) {
        throw new Error('The "cartId" parameter is required.');
      }

      // Call API to fetch the cart by ID
      const { data, error } = await useFetch<GetCartbyIDResponse>(`/api/carts/${cartId}`, {
        query: currency ? { currency } : undefined, // Include currency if provided
        key: `getcart-${cartId}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to retrieve cart:', error.value);
        throw new Error('Failed to retrieve cart.');
      }

      // Optionally update the store with the fetched cart
      if (useStore && data.value?.success) {
        cartStore.cart = data.value.cart;
      }

      // Return the cart response
      return data.value as GetCartbyIDResponse;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to retrieve cart:', error.message || error);
      throw new Error('Failed to retrieve cart. Please try again later.');
    }
  };

  /**
   * Retrieve all carts with filters
   */
  const getCarts = async (
    params: GetCartsRequest,
    useStore = true
  ): Promise<GetCartsResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<GetCartsResponse>('/api/carts', {
        params: {
          currency: params.currency,
          page: params.pageable.page,
          size: params.pageable.size,
          status: params.status ? JSON.stringify(params.status) : undefined,
          type: 'CART',
        },
        key: `getcarts-${JSON.stringify(params)}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to retrieve carts:', error.value);
        return null;
      }

      // Optionally update the store with the fetched carts
      if (useStore) {
        cartStore.carts = data.value?.carts || [];
      }
      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while retrieving carts:', error);
      return null;
    }
  };

  const getCartOrCreate = async (
    params: GetCartsRequest,
    useStore = true
  ): Promise<Cart | CartWithTotals> => {
    try {
      await ensureAuthenticated();

      // Fetch carts and check if any exist
      const cartsResponse = await getCarts(params, useStore);
      const filteredCarts = cartsResponse?.carts;

      // Return the first cart if available
      if (filteredCarts && filteredCarts.length > 0) {
        const firstCart = filteredCarts[0];
        if (useStore) {
          cartStore.cart = firstCart;
        }

        // Fetch cart lines for the first cart
        await getCartLines(
          {
            cartId: firstCart.id,
            currency: currency,
            pageable: { page: 0, size: defaultPaginationSize },
          },
          useStore
        );

        return firstCart;
      } else {
        // Create a new cart if none exist
        const newCart = await createCart({ name: 'Default Cart' }, useStore);
        if (useStore && newCart?.success) {
          cartStore.cart = newCart.cart;
          cartStore.cartLines = [];
          return newCart.cart;
        } else {
          throw new Error('Failed to create a new cart.');
        }
      }
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to get or create cart:', error);
      throw new Error('Failed to get or create cart. Please try again later.');
    }
  };

  /**
   * Create a new cart
   */
  const createCart = async (
    params: CreateCartRequest,
    useStore = true
  ): Promise<CreateCartResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<CreateCartResponse>('/api/carts/create', {
        method: 'POST',
        body: params,
        key: `createcart-${params.name}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to create cart:', error.value);
        return null;
      }

      // Optionally update the store with the created cart
      if (useStore) {
        cartStore.cart = data.value || null;
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while creating cart:', error);
      return null;
    }
  };

  /**
   * Update a cart by its ID
   */
  const updateCart = async (params: UpdateCartRequest): Promise<void> => {
    try {
      await ensureAuthenticated();

      // Validate parameters
      const { cartId, name, type } = params;
      if (!cartId || !name) {
        throw new Error('Both "cartId" and "name" parameters are required.');
      }

      // Call the API to update the cart
      const { data, error } = await useFetch(`/api/carts/${cartId}/update`, {
        method: 'PUT',
        body: { name, type },
        key: `updatecart-${cartId}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update cart:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error('Missing cart id or cart name already exists.');
          case 404:
            throw new Error('Cart not found.');
          default:
            throw new Error('Failed to update cart.');
        }
      }

      // Validate response format
      // if (data.value && typeof data.value === 'string') {
      //   return data.value;
      // } else {
      //   throw new Error('Unexpected response format.');
      // }
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update cart:', error.message || error);
      throw new Error('An unexpected error occurred while updating the cart.');
    }
  };

  /**
   * Delete a cart by its ID
   */
  const deleteCart = async (
    cartId: string,
    useStore = true
  ): Promise<DeleteCartResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to delete the cart
      const { data, error } = await useFetch<DeleteCartResponse>(`/api/carts/${cartId}/delete`, {
        method: 'DELETE',
        key: `deletecart-${cartId}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to delete cart:', error.value);
        throw new Error('Failed to delete cart.');
      }

      // Optionally update the store
      if (data.value && useStore) {
        cartStore.cart = cartStore.cart?.id === cartId ? null : cartStore.cart;
        cartStore.carts = cartStore.carts.filter((cart) => cart.id !== cartId);
      }
      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to delete cart:', error.message || error);
      throw new Error('An unexpected error occurred while deleting the cart.');
    }
  };

  /**
   * Initialize orders from cart
   */
  const initializeOrdersFromCart = async (cartId: string): Promise<string | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to initialize orders from the cart
      const { data, error } = await useFetch<InitializeOrderResponse>(
        `/api/carts/${cartId}/initializeOrder`,
        {
          method: 'POST',
          key: `initializeOrder-${cartId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to initialize orders from cart:', error.value);
        throw new Error('Failed to initialize orders from cart.');
      }

      return data || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to initialize orders from cart:', error.message || error);
      return null;
    }
  };

  /**
   * Fetch cart lines by cart ID
   */
  const getCartLines = async (
    params: GetCartLinesRequest,
    useStore = true
  ): Promise<GetCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call API to fetch cart lines by cart ID
      const { data, error } = await useFetch<GetCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines`,
        {
          query: {
            currency: params.currency,
            page: params.pageable.page,
            size: params.pageable.size || defaultPaginationSize,
            supplierId: params.supplierId ? JSON.stringify(params.supplierId) : undefined,
            productVariantId: params.productVariantId
              ? JSON.stringify(params.productVariantId)
              : undefined,
            offerPriceId: params.offerPriceId ? JSON.stringify(params.offerPriceId) : undefined,
          },
          key: `getcartlines-${params.cartId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to get cart lines:', error.value);
        throw new Error('Failed to get cart lines.');
      }

      // Optionally update the store with fetched cart lines
      if (useStore && data.value) {
        cartStore.cartLines = (data.value?.cartLines as any) || [];
      }

      // Return the response
      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while getting cart lines:', error.message || error);
      throw new Error('An unexpected error occurred while getting cart lines.');
    }
  };

  /**
   * Delete cart lines
   */
  const deleteCartLines = async (
    params: DeleteCartLinesRequest,
    useStore = true
  ): Promise<DeleteCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to delete cart lines
      const { data, error } = await useFetch<DeleteCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines/delete`,
        {
          method: 'DELETE',
          body: params,
          key: `deleteCartLines-${params.cartId}-${params.lineIdsToDelete}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to delete cart lines:', error.value);
        throw new Error('Failed to delete cart lines.');
      }

      // Optionally update the store
      if (useStore) {
        await refreshCartStore(params.cartId, currency);
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to delete cart lines:', error.message || error);
      throw new Error('An unexpected error occurred while deleting cart lines.');
    }
  };

  /**
   * Clear cart by deleting it and creating a new one
   */
  const clearCart = async (cartId: string, useStore = true): Promise<void> => {
    try {
      await ensureAuthenticated();

      // Call the API to clear the cart
      const { data, error } = await useFetch<CreateCartResponse>(`/api/carts/${cartId}/clear`, {
        method: 'DELETE',
        key: `clearCart-${cartId}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to clear cart:', error.value);
        throw new Error('Failed to clear cart.');
      }

      // Optionally update the store with the new cart
      if (useStore && data.value) {
        cartStore.cart = data.value.cart;
        cartStore.cartLines = [];
      }
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to clear cart:', error.message || error);
      throw new Error('An unexpected error occurred while clearing the cart.');
    }
  };

  /**
   * Update cart lines
   */
  const updateCartLines = async (
    params: UpdateCartLinesRequest,
    useStore = true
  ): Promise<UpdateCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to update cart lines
      const { data, error } = await useFetch<UpdateCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines/update`,
        {
          method: 'PUT',
          body: params,
          key: `updateCartLines-${params.cartId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update cart lines:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error(
              'Quantity lower or equals to 0 / missing quantity / missing offerPriceId.'
            );
          case 404:
            throw new Error('Cart not found.');
          case 412:
            throw new Error('Impossible to add any cart line.');
          default:
            throw new Error('Failed to update cart lines.');
        }
      }

      // Optionally update the store
      if (useStore && data.value) {
        cartStore.cartLines = cartStore.cartLines.map(
          (line) =>
            params.linesToUpdate.find(
              (updatedLine) => updatedLine.offerPriceId === line.offer.offerPriceId
            ) || line
        );
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update cart lines:', error.message || error);
      throw new Error('An unexpected error occurred while updating cart lines.');
    }
  };

  /**
   * Update cart lines by variant
   */
  const updateCartLinesByVariant = async (
    params: UpdateCartLinesByVariantRequest,
    useStore = true
  ): Promise<UpdateCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to update cart lines by variant
      const { data, error } = await useFetch<UpdateCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines/update-by-variant`,
        {
          method: 'PUT',
          body: params,
          key: `updateCartLinesByVariant-${params.cartId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update cart lines by variant:', error.value);
        throw new Error('Failed to update cart lines by variant.');
      }

      // Optionally update the store
      if (useStore && data.value) {
        cartStore.cartLines = cartStore.cartLines.map(
          (line) =>
            params.linesToUpdate.find(
              (updatedLine) => updatedLine.productVariantId === line.productVariant?.id
            ) || line
        );
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update cart lines by variant:', error.message || error);
      throw new Error('An unexpected error occurred while updating cart lines by variant.');
    }
  };

  /**
   * Add single or multiple products to the cart and refresh the store.
   */
  const addOrUpdateCartLine = async (
    params: AddOrUpdateCartLineRequest,
    useStore = true
  ): Promise<UpdateCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();
      console.log('🔍 params:', params);

      // Construct lines to update
      const lines = params.lines.map((line) => ({
        quantity: line.quantity,
        updateAction: line.updateAction,
        ...(params.typeId === 'offerPriceId'
          ? { offerPriceId: line.offerPriceId }
          : { productVariantId: line.variantId }),
      }));

      const requestParams = {
        cartId: params.cartId,
        typeId: params.typeId,
        lines,
      };
      console.log('requestParams', requestParams);
      // Call the API to add or update cart lines
      const { data, error } = await useFetch<UpdateCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines/add`,
        {
          method: 'PUT',
          body: requestParams,
          key: `addOrUpdateCartLine-${Math.random()}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to add or update cart lines:', error.value);
        throw new Error('Failed to add or update cart lines.');
      }

      // Optionally refresh the store
      if (useStore !== false) {
        await refreshCartStore(params.cartId, currency);
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to add or update cart lines:', error.message || error);
      throw new Error('An unexpected error occurred while adding or updating cart lines.');
    }
  };

  /**
   * Refresh the cart store with the latest data from the backend.
   */
  const refreshCartStore = async (cartId: string, currency: Currency): Promise<void> => {
    try {
      await ensureAuthenticated();

      // Fetch cart and cart lines concurrently
      const [cartResponse, cartLinesResponse] = await Promise.all([
        getCartbyId({ cartId, currency }, false),
        getCartLines(
          { cartId, currency, pageable: { page: 0, size: defaultPaginationSize } },
          false
        ),
      ]);

      // Validate responses
      if (!cartResponse || !cartLinesResponse) {
        throw new Error('Failed to fetch cart or cart lines.');
      }

      // Update the store with the latest cart and cart lines data
      cartStore.cart = cartResponse.cart;
      cartStore.cartLines = (cartLinesResponse.cartLines as any) || [];
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to refresh cart store:', error.message || error);
      throw new Error('An unexpected error occurred while refreshing the cart store.');
    }
  };
  return {
    getCartbyId,
    getCarts,
    createCart,
    updateCart,
    deleteCart,
    clearCart,
    getCartOrCreate,
    initializeOrdersFromCart,
    getCartLines,
    deleteCartLines,
    updateCartLines,
    updateCartLinesByVariant,
    addOrUpdateCartLine,
    refreshCartStore,
  };
}
